import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Typography, CircularProgress, Grid, Box, useTheme } from "@mui/material";
import { Account, Vehicle } from "../state/types";
import { useNavigate } from "react-router-dom";
import { getVehiclesFromDb } from "../api/getVehiclesFromDb";
import { getAssetErrors } from "../api/getErrors";
import { userHasAccess } from "../services/RoleBasedAccess";
import { State } from '../state/reducers';
import { useSelector } from "react-redux";
import VehicleInfoSidebar from "../components/vehicle/VehicleSidebar";
import VehicleMediaDisplay from "../components/vehicle/VehicleMediaDisplay";
import ProcessingErrorList from "../components/vehicle/ProcessingErrorList";
import VehicleHeader from "../components/vehicle/VehicleHeader";
import VehicleArchivedBanner from "../components/vehicle/VehicleArchivedBanner";

function getUndercarriageImageInfo(vehicle: Vehicle) {
  const hasDewarp = !!vehicle.imageUndercarriageDewarp?.length;
  const fallback = vehicle.imageUndercarriage || [];

  return {
    key: "uc",
    label: hasDewarp ? "Undercarriage" : "Raw Undercarriage",
    images: hasDewarp ? vehicle.imageUndercarriageDewarp : fallback,
    visible: hasDewarp || !!fallback.length
  };
}

const VehicleScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate()
  const user: Account = useSelector((state: State) => state.accountState.user);
  const userLoaded = !!user && !!user.role
  const { assetId, clientId } = useParams<{ assetId: string; clientId: string }>();
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [visibleTypes, setVisibleTypes] = useState<{ [key: string]: boolean }>({});
  const [errors, setErrors] = useState<any[]>([]);

  useEffect(() => {
    if (!userLoaded) return
    if (!clientId || !assetId) {
      navigate("/");
      return;
    }

    const baseClientId = clientId.replace(/U\d{2}$/, '');
    const isInternalUser = userHasAccess(user?.role, ["Internal"]);

    if (!isInternalUser && !user?.system_access.includes(baseClientId)) {
      navigate("/");
    }
  }, [user, clientId]);

  useEffect(() => {
    if (assetId) {
      const vinOrStock = assetId.split("-")[0];
      document.title = vinOrStock;
    } else {
      document.title = "Black Widow UMS";
    }

    return () => {
      document.title = "Black Widow UMS"; // restore default on unmount
    };
  }, [assetId]);


  useEffect(() => {
    const getVehicleData = async () => {
      if (!clientId || !assetId) {
        navigate("/");
        return;
      }
      try {
        const body = {
          page: 1,
          per_page: 1,
          system_code: clientId,
          asset_id: assetId,
          archived: true
        };

        const { vehicles } = await getVehiclesFromDb(body)
        const vehicle = vehicles[0]
        setVehicle(vehicle);

        const errorResponse = await getAssetErrors(clientId, assetId.split("-")[0]);
        setErrors(errorResponse.errors || []);

        const initialVisibleTypes = {
          exterior: !!vehicle.imageExterior?.length,
          crop: !!vehicle.imageCrop?.length,
          featured: !!vehicle.imageFeatured?.length,
          manual: !!vehicle.imageManual?.length,
          pano: !!vehicle.imagePano?.length,
          uc: !!vehicle.imageUndercarriageDewarp?.length || !!vehicle.imageUndercarriage?.length,
          bgr: !!vehicle.imageBgr?.length,
        };
        setVisibleTypes(initialVisibleTypes);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching vehicle data:", error);
      }
    };

    getVehicleData();
  }, [clientId, assetId]);

  if (loading) {
    return <CircularProgress />;
  }

  if (!vehicle || !clientId || !assetId) {
    return <Typography variant="h6">Vehicle not found</Typography>;
  }

  const ucInfo = getUndercarriageImageInfo(vehicle);

  const imageCategories = [
    { key: "exterior", label: "Exterior", images: vehicle.imageExterior || [] },
    { key: "crop", label: "Cropped", images: vehicle.imageCrop || [] },
    { key: "featured", label: "Featured", images: vehicle.imageFeatured || [] },
    { key: "bgr", label: "Background Replaced", images: vehicle.imageBgr || [] },
    { key: "manual", label: "Manual", images: vehicle.imageManual || [] },
    { key: "pano", label: "Panoramic", images: vehicle.imagePano || [] },
    ucInfo,
  ];

  const BASE_HEADER_HEIGHT = 150; // total height for header area normally
  const ARCHIVED_BANNER_HEIGHT = 50; // height of red archived banner
  const hasArchivedBanner = vehicle.archived === true;

  // Dynamically compute max content height
  const CONTENT_MAX_HEIGHT = `calc(100vh - ${BASE_HEADER_HEIGHT + (hasArchivedBanner ? ARCHIVED_BANNER_HEIGHT : 0)}px)`;


  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, p: 3 }}>
      {vehicle.archived && <VehicleArchivedBanner />}

      <VehicleHeader vehicle={vehicle} />

      <Grid container
        sx={{
          maxHeight: CONTENT_MAX_HEIGHT,
          overflow: 'auto'
        }}>
        <Grid item
          xs={12}
          md={3}
          sx={{
            maxHeight: CONTENT_MAX_HEIGHT,
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" }
          }}
        >
          <VehicleInfoSidebar
            vehicle={vehicle}
            visibleTypes={visibleTypes}
            setVisibleTypes={setVisibleTypes}
            imageCategories={imageCategories}
          />
        </Grid>



        <Grid
          item
          xs={12}
          md={9}
          sx={{
            maxHeight: CONTENT_MAX_HEIGHT,
            overflowY: "auto",
            p: 3,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" }
          }}
        >
          <VehicleMediaDisplay
            clientId={clientId}
            assetId={assetId}
            visibleTypes={visibleTypes}
            imageCategories={imageCategories}
          />

          <ProcessingErrorList errors={errors} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default VehicleScreen;